/**
 * Contact-sheet page-count math — the JS twin of
 * functions-py/contact_sheet/pagecount.py. The Groups and Proof screens show
 * "≈ N pages" before anything builds, so the estimate must match what the
 * Python builder actually produces. Keep the two in sync (same page size,
 * margins, and 3-col overview grid).
 *
 * A group with K captures = ceil(K / overviewPerPage) overview pages + K detail
 * pages (one per capture). The whole PDF is the sum across included groups.
 */

const PAGE_W = 215.9; // US Letter portrait, mm
const PAGE_H = 279.4;
const MARGIN = 15;

const COLS = 3;
const CELL_GAP = 6;
const CAPTION_H = 12;
const TITLE_BLOCK_H = 17;
const HEADER_Y = 32;
const FOOTER_MARGIN = 18;

/** Key-photo cells per overview page (dynamic rows; ~9 at the approved layout). */
export function overviewPerPage() {
  const contentW = PAGE_W - MARGIN * 2;
  const cellW = (contentW - CELL_GAP * (COLS - 1)) / COLS;
  const rowHeight = cellW + CAPTION_H;
  const availH = PAGE_H - HEADER_Y - TITLE_BLOCK_H - FOOTER_MARGIN;
  const rows = Math.max(1, Math.floor(availH / rowHeight));
  return rows * COLS;
}

export function overviewPages(numCaptures) {
  if (numCaptures <= 0) return 0;
  return Math.ceil(numCaptures / overviewPerPage());
}

export function groupPageCount(numCaptures) {
  if (numCaptures <= 0) return 0;
  return overviewPages(numCaptures) + numCaptures;
}

/** Total pages across groups (array of per-group capture counts). */
export function pdfPageCount(groupCaptureCounts) {
  return groupCaptureCounts.reduce((sum, n) => sum + groupPageCount(n), 0);
}

/**
 * C4 — THE inclusion definition, shared by the UI estimate and the Python
 * builder. A capture is in the contact sheet iff its group is confirmed AND it
 * has stored picks (`selected.length > 0`). This MUST match
 * functions-py/main.py `_load_job` (confirmed groups; `captures` filtered to
 * those with a non-empty `selected`). Returns per-included-group capture counts
 * (empty groups dropped), ready to feed `pdfPageCount`.
 */
export function includedCaptureCounts(groups) {
  return (groups || [])
    .filter((g) => g.isConfirmed)
    .map((g) => (g.captures || []).filter((c) => (c.selected || []).length > 0).length)
    .filter((n) => n > 0);
}
